import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  chatScopeParams,
  getCentrifugoWsUrl,
  getChatToken
} from '../../helper/chat';
import { CHAT_EVENT_MESSAGE_NEW } from './constants';
import {
  applyChatRealtimeMessage,
  clearChatThread,
  getChatConversations,
  getChatUnread,
  setChatHasUnread
} from '../../redux/actions/chat';
import {
  getAllBuildings,
  setGlobalBuildingId,
  setGlobalSelectedBuilding
} from '../../redux/actions/building';
import { setLoginFalse } from '../../redux/actions/auth';
import { UserRole } from '../users/constant';
import ChatRealtimeClient from './chatRealtimeClient';
import { refreshChatSessionTokens } from './chatSessionTokens';
import { getChatInboxSearchQ } from './chatInboxScope';
import { parseConversations } from './chatReducerHelpers';
import { useChatEnabled } from '../../hooks/useChatEnabled';

const buildingsFromGetAllResponse = (res) => {
  const payload = res?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const ChatRealtime = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userId = useSelector((state) => state.auth.user_id);
  const roleId = useSelector((state) => state.auth.roleId);
  const authOrgId = useSelector((state) => state.auth.orgId);
  const selectedConversation = useSelector((state) => state.chat.selectedConversation);
  const conversations = useSelector((state) => state.chat.conversations);
  const globalBuildingId = useSelector((state) => state.building.globalBuildingId);
  const globalOrgId = useSelector((state) => state.Organization.globalOrgId);
  const chatEnabled = useChatEnabled();

  const clientRef = useRef(null);
  const selectedConversationRef = useRef(selectedConversation);
  const conversationsRef = useRef(conversations);
  const buildingIdRef = useRef(Number(globalBuildingId) || 0);
  const orgIdRef = useRef(Number(globalOrgId) > 0 ? Number(globalOrgId) : 0);
  const roleIdRef = useRef(Number(roleId) || 0);
  const authOrgIdRef = useRef(Number(authOrgId) || 0);

  // Keep scope refs in sync during render so a Centrifugo scope_refreshed refetch
  // cannot race ahead of useEffect and request the previous building.
  buildingIdRef.current = Number(globalBuildingId) || 0;
  orgIdRef.current = Number(globalOrgId) > 0 ? Number(globalOrgId) : 0;
  roleIdRef.current = Number(roleId) || 0;
  authOrgIdRef.current = Number(authOrgId) || Number(localStorage.getItem('orgId')) || 0;

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    if (isLoggedIn !== true || !chatEnabled) return;
    dispatch(
      getChatUnread(
        chatScopeParams({
          building: globalBuildingId,
          org: globalOrgId
        })
      )
    );
  }, [dispatch, isLoggedIn, chatEnabled, globalBuildingId, globalOrgId]);

  useEffect(() => {
    if (isLoggedIn !== true || !chatEnabled) {
      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
      }
      if (!chatEnabled) {
        dispatch(setChatHasUnread(false));
        dispatch(clearChatThread());
      }
      return undefined;
    }

    const client = new ChatRealtimeClient();
    clientRef.current = client;
    let inboxRefetchTimer = null;

    const refetchInboxScope = ({ immediate = false } = {}) => {
      const run = () => {
        inboxRefetchTimer = null;
        const scope = chatScopeParams({
          building: buildingIdRef.current,
          org: orgIdRef.current,
          q: getChatInboxSearchQ()
        });
        dispatch(getChatUnread(scope));
        // Hard replace so conversations outside the new scope do not linger.
        dispatch(
          getChatConversations({
            page: 1,
            replace: true,
            ...scope,
            success: (res) => {
              const selectedId = Number(selectedConversationRef.current?.id) || 0;
              if (!selectedId) return;
              const rows = parseConversations(res?.data);
              const stillVisible = rows.some((c) => Number(c.id) === selectedId);
              if (!stillVisible) {
                dispatch(clearChatThread());
              }
            }
          })
        );
      };

      // Debounce event-driven refetches so they do not cancel in-flight inbox
      // pagination (takeLatest) right after open / load-more.
      if (immediate) {
        if (inboxRefetchTimer) {
          clearTimeout(inboxRefetchTimer);
          inboxRefetchTimer = null;
        }
        run();
        return;
      }
      if (inboxRefetchTimer) clearTimeout(inboxRefetchTimer);
      inboxRefetchTimer = setTimeout(run, 400);
    };

    const resolveOrgIdForBuildings = () => {
      const role = roleIdRef.current;
      const globalOrg = orgIdRef.current;
      if ((role === UserRole.SUPER_ADMIN || role === UserRole.ADMIN) && globalOrg > 0) {
        return globalOrg;
      }
      return authOrgIdRef.current || globalOrg || 0;
    };

    const clearThreadIfBuildingOutOfScope = (accessibleBuildings) => {
      const selected = selectedConversationRef.current;
      if (!selected?.id) return;

      const convBuilding = Number(selected.building_id) || 0;
      const allowedIds = new Set(
        (accessibleBuildings || []).map((b) => Number(b.id)).filter((id) => id > 0)
      );
      const currentFilter = buildingIdRef.current;

      const buildingStillAssigned =
        allowedIds.size === 0 || convBuilding <= 0 || allowedIds.has(convBuilding);
      const matchesFilter =
        currentFilter <= 0 || convBuilding <= 0 || convBuilding === currentFilter;

      if (!buildingStillAssigned || !matchesFilter) {
        dispatch(clearChatThread());
      }
    };

    const applyBuildingSelection = (list) => {
      const current = buildingIdRef.current;
      const stillValid = current > 0 && list.some((b) => Number(b.id) === current);
      if (!stillValid) {
        const next = list[0];
        const nextId = Number(next?.id) || 0;
        const nextName = next?.name || '';
        buildingIdRef.current = nextId;
        dispatch(
          setGlobalBuildingId({
            globalBuildingId: nextId,
            globalBuildingName: nextId === 0 ? '' : nextName
          })
        );
        dispatch(
          setGlobalSelectedBuilding(
            nextId === 0
              ? { value: 0, label: 'All Buildings', id: 0 }
              : { value: nextId, label: nextName, id: nextId, name: nextName }
          )
        );
      }
      clearThreadIfBuildingOutOfScope(list);
    };

    // Assigned-building changes remint chatToken but do not reload accessible buildings.
    // Refresh buildings first so chat filter / New Chat pickers match the new scope.
    const refreshBuildingsThenInbox = () => {
      const orgForBuildings = resolveOrgIdForBuildings();
      if (orgForBuildings <= 0) {
        refetchInboxScope({ immediate: false });
        return;
      }

      dispatch(
        getAllBuildings({
          id: orgForBuildings,
          success: (res) => {
            applyBuildingSelection(buildingsFromGetAllResponse(res));
            refetchInboxScope({ immediate: false });
          },
          fail: () => {
            refetchInboxScope({ immediate: false });
          }
        })
      );
    };

    const start = async () => {
      const wsUrl = getCentrifugoWsUrl();
      if (!wsUrl) {
        console.error(
          '[chat] REACT_APP_CENTRIFUGO_WS_URL is not set; skipping realtime connect'
        );
        return;
      }

      if (!getChatToken()) {
        try {
          await refreshChatSessionTokens();
        } catch (e) {
          console.warn('[chat] failed to refresh chat session tokens before connect', e);
          return;
        }
      }
      if (clientRef.current !== client) return;

      client.connect({
        url: wsUrl,
        onEvent: ({ data }) => {
          if (!data || data.type !== CHAT_EVENT_MESSAGE_NEW) return;
          const message = data.message;
          const conversation = data.conversation;
          if (!message || !conversation) return;

          const currentUserId = Number(userId || localStorage.getItem('user_id')) || 0;
          const convBuilding = Number(conversation.building_id) || 0;
          const convOrg = Number(conversation.org_id) || 0;
          const filterBuilding = buildingIdRef.current;
          const filterOrg = orgIdRef.current;
          // JWT may still deliver other assigned buildings; ignore for the filtered UI.
          const matchesBuilding =
            filterBuilding <= 0 || convBuilding <= 0 || convBuilding === filterBuilding;
          const matchesOrg =
            filterOrg <= 0 || convOrg <= 0 || convOrg === filterOrg;
          if (!matchesBuilding || !matchesOrg) {
            return;
          }

          dispatch(
            applyChatRealtimeMessage({
              message,
              conversation,
              currentUserId
            })
          );

          const isOwn = currentUserId > 0 && Number(message.sender_user_id) === currentUserId;
          const isViewing =
            selectedConversationRef.current?.id != null &&
            Number(selectedConversationRef.current.id) === Number(conversation.id);
          if (!isOwn && !isViewing) {
            dispatch(setChatHasUnread(true));
          }

          const existing = (conversationsRef.current || []).find(
            (c) => Number(c.id) === Number(conversation.id)
          );
          if (!existing?.resident_name && !conversation.resident_name) {
            refetchInboxScope();
          }
        },
        onStatus: (status) => {
          if (status === 'terminal_disconnect') {
            // Match session-expired behavior: drop local auth and force login.
            localStorage.clear();
            dispatch(setLoginFalse());
            window.location.assign('/login');
            return;
          }
          if (status === 'scope_refreshed') {
            // Reload accessible buildings, then hard-replace inbox for the new scope.
            refreshBuildingsThenInbox();
          }
        }
      });
    };

    start();

    return () => {
      if (inboxRefetchTimer) clearTimeout(inboxRefetchTimer);
      client.disconnect();
      if (clientRef.current === client) clientRef.current = null;
    };
  }, [dispatch, isLoggedIn, chatEnabled, userId]);

  return null;
};

export default ChatRealtime;
